import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Typography,
    Box
} from '@mui/material';
import { addYears, format, startOfToday } from 'date-fns';
import { RxService } from '../../../../service/rxService';
import { toast } from 'react-toastify';
import SimpleDatePicker from '../../../ui/simpleDatePicker';
import { SelectPractioner } from '../../../common';
import { axisValues, addValues } from '../../../../utils/rxValues';
import SignedValueSelector from './SignedValueSelector';
import {
    SPHERE_MIN,
    SPHERE_MAX,
    SPHERE_INCREMENT,
    CYLINDER_MIN,
    CYLINDER_MAX,
    CYLINDER_INCREMENT
} from '../../../../utils/prescriptionConstants';

const CreateRxInlineDialog = ({ open, onClose, onRxCreated, customerId }) => {
    const [rxData, setRxData] = useState({
        customerId: customerId,
        practitionerId: null,
        rxDate: format(startOfToday(), 'yyyy-MM-dd'),
        expiryDate: format(addYears(startOfToday(), 1), 'yyyy-MM-dd'),
        rxType: 'glasses',
        // OD values
        odSph: '',
        odCyl: '',
        odAxis: '',
        odAdd: '',
        odPrism: '',
        odPrismBase: '',
        odDistPd: '',
        odNearPd: '',
        odSegHt: '',
        odSpBc: '',
        odVtOc: '',
        odLensDiameter: '',
        odBc: '',
        odContactLensDiameter: '',
        odK1: '',
        odK2: '',
        // OS values
        osSph: '',
        osCyl: '',
        osAxis: '',
        osAdd: '',
        osPrism: '',
        osPrismBase: '',
        osDistPd: '',
        osNearPd: '',
        osSegHt: '',
        osSpBc: '',
        osVtOc: '',
        osLensDiameter: '',
        osBc: '',
        osContactLensDiameter: '',
        osK1: '',
        osK2: '',
        status: 'active'
    });
    const [saving, setSaving] = useState(false);

    const handleFieldChange = (field, value) => {
        setRxData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        // Validate required fields
        if (!rxData.practitionerId) {
            toast.error('Please select a practitioner');
            return;
        }

        if (!rxData.odSph && !rxData.osSph) {
            toast.error('Please enter at least one sphere value');
            return;
        }

        setSaving(true);
        try {
            const response = await RxService.createRx({
                ...rxData,
                customerId: customerId
            });

            const { code, data, message } = response;
            if (code === 0) {
                toast.success('RX created successfully');
                onRxCreated(data);
                onClose();
            } else {
                toast.error(message || 'Failed to create RX');
            }
        } catch (error) {
            console.error('Error creating RX:', error);
            toast.error('Failed to create RX');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Create New RX</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {/* Practitioner and Dates */}
                    <Grid item xs={12}>
                        <SelectPractioner
                            setPractitioner={(practitioner) => handleFieldChange('practitionerId', practitioner)}
                            practitioner={rxData.practitionerId}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SimpleDatePicker
                            label="RX Date"
                            onChange={(date) => handleFieldChange('rxDate', date)}
                            value={rxData.rxDate}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <SimpleDatePicker
                            label="Expiry Date"
                            onChange={(date) => handleFieldChange('expiryDate', date)}
                            value={rxData.expiryDate}
                        />
                    </Grid>

                    {/* Rx Type selector (glasses or contacts) */}
                    <Grid item xs={12}>
                        <FormControl>
                            <FormLabel>Rx Type</FormLabel>
                            <RadioGroup
                                row
                                name="rxType"
                                value={rxData.rxType}
                                onChange={(e) => handleFieldChange('rxType', e.target.value)}
                            >
                                <FormControlLabel value="glasses" control={<Radio />} label="Glasses" />
                                <FormControlLabel value="contacts" control={<Radio />} label="Contacts" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {/* OD (Right Eye) */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                            OD (Right Eye)
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                Sphere
                            </Typography>
                            <SignedValueSelector
                                name="odSph"
                                value={rxData.odSph}
                                min={SPHERE_MIN}
                                max={SPHERE_MAX}
                                step={SPHERE_INCREMENT}
                                onChange={handleFieldChange}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                Cylinder
                            </Typography>
                            <SignedValueSelector
                                name="odCyl"
                                value={rxData.odCyl}
                                min={CYLINDER_MIN}
                                max={CYLINDER_MAX}
                                step={CYLINDER_INCREMENT}
                                onChange={handleFieldChange}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Axis</InputLabel>
                            <Select
                                value={rxData.odAxis}
                                onChange={(e) => handleFieldChange('odAxis', e.target.value)}
                                label="Axis"
                            >
                                <MenuItem value="">-</MenuItem>
                                {axisValues.map(val => (
                                    <MenuItem key={val} value={val}>{val}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Add</InputLabel>
                            <Select
                                value={rxData.odAdd}
                                onChange={(e) => handleFieldChange('odAdd', e.target.value)}
                                label="Add"
                            >
                                <MenuItem value="">-</MenuItem>
                                {addValues.map(val => (
                                    <MenuItem key={val} value={val}>+{val}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* OS (Left Eye) */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                            OS (Left Eye)
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                Sphere
                            </Typography>
                            <SignedValueSelector
                                name="osSph"
                                value={rxData.osSph}
                                min={SPHERE_MIN}
                                max={SPHERE_MAX}
                                step={SPHERE_INCREMENT}
                                onChange={handleFieldChange}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                Cylinder
                            </Typography>
                            <SignedValueSelector
                                name="osCyl"
                                value={rxData.osCyl}
                                min={CYLINDER_MIN}
                                max={CYLINDER_MAX}
                                step={CYLINDER_INCREMENT}
                                onChange={handleFieldChange}
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Axis</InputLabel>
                            <Select
                                value={rxData.osAxis}
                                onChange={(e) => handleFieldChange('osAxis', e.target.value)}
                                label="Axis"
                            >
                                <MenuItem value="">-</MenuItem>
                                {axisValues.map(val => (
                                    <MenuItem key={val} value={val}>{val}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Add</InputLabel>
                            <Select
                                value={rxData.osAdd}
                                onChange={(e) => handleFieldChange('osAdd', e.target.value)}
                                label="Add"
                            >
                                <MenuItem value="">-</MenuItem>
                                {addValues.map(val => (
                                    <MenuItem key={val} value={val}>+{val}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Additional OD Fields (vary by Rx Type) */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                            OD Additional Measurements
                        </Typography>
                    </Grid>
                    {rxData.rxType === 'glasses' ? (
                        <>
                            <Grid item xs={4}>
                                <TextField
                                    label="Dist PD"
                                    size="small"
                                    fullWidth
                                    value={rxData.odDistPd}
                                    onChange={(e) => handleFieldChange('odDistPd', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Near PD"
                                    size="small"
                                    fullWidth
                                    value={rxData.odNearPd}
                                    onChange={(e) => handleFieldChange('odNearPd', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Seg Ht"
                                    size="small"
                                    fullWidth
                                    value={rxData.odSegHt}
                                    onChange={(e) => handleFieldChange('odSegHt', e.target.value)}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={3}>
                                <TextField
                                    label="BC"
                                    size="small"
                                    fullWidth
                                    value={rxData.odBc}
                                    onChange={(e) => handleFieldChange('odBc', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label="Diameter"
                                    size="small"
                                    fullWidth
                                    value={rxData.odContactLensDiameter}
                                    onChange={(e) => handleFieldChange('odContactLensDiameter', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label="K1"
                                    size="small"
                                    fullWidth
                                    value={rxData.odK1}
                                    onChange={(e) => handleFieldChange('odK1', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label="K2"
                                    size="small"
                                    fullWidth
                                    value={rxData.odK2}
                                    onChange={(e) => handleFieldChange('odK2', e.target.value)}
                                />
                            </Grid>
                        </>
                    )}

                    {/* Additional OS Fields (vary by Rx Type) */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 1 }}>
                            OS Additional Measurements
                        </Typography>
                    </Grid>
                    {rxData.rxType === 'glasses' ? (
                        <>
                            <Grid item xs={4}>
                                <TextField
                                    label="Dist PD"
                                    size="small"
                                    fullWidth
                                    value={rxData.osDistPd}
                                    onChange={(e) => handleFieldChange('osDistPd', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Near PD"
                                    size="small"
                                    fullWidth
                                    value={rxData.osNearPd}
                                    onChange={(e) => handleFieldChange('osNearPd', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Seg Ht"
                                    size="small"
                                    fullWidth
                                    value={rxData.osSegHt}
                                    onChange={(e) => handleFieldChange('osSegHt', e.target.value)}
                                />
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Grid item xs={3}>
                                <TextField
                                    label="BC"
                                    size="small"
                                    fullWidth
                                    value={rxData.osBc}
                                    onChange={(e) => handleFieldChange('osBc', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label="Diameter"
                                    size="small"
                                    fullWidth
                                    value={rxData.osContactLensDiameter}
                                    onChange={(e) => handleFieldChange('osContactLensDiameter', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label="K1"
                                    size="small"
                                    fullWidth
                                    value={rxData.osK1}
                                    onChange={(e) => handleFieldChange('osK1', e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label="K2"
                                    size="small"
                                    fullWidth
                                    value={rxData.osK2}
                                    onChange={(e) => handleFieldChange('osK2', e.target.value)}
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                >
                    {saving ? 'Creating...' : 'Create RX'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateRxInlineDialog;
